import * as React from 'react'
import './LogPointsApplicationsBody.css'
import { LogPointsTopBar, Props as TopBarProps } from '../common/LogPointsTopBar/LogPointsTopBar'
import { buildLogSearchRedirectionLink } from '@mulesoft/monitoring-center-components/lib/utils/LogSearchRedirectionHelper'
import { LOG_LEVEL_OPTIONS, LogLevel, LogPointsConnectorsState } from './model'
import LogPointsConnectorsList, { Props as ConnectorsListProps } from './LogPointsConnectorsList/LogPointsConnectorsList'
import Spinner from '@mulesoft/anypoint-components/lib/Spinner'
import { getResourceMuleVersion, isARMHybridDetail,isCloudHub2Application,getResourceName, Resource } from '../../../../common/resource/model'
import TextField from '@mulesoft/anypoint-components/lib/TextField'
import { LogPointsType } from '../../LogPointsPage/model'
import { parseSemver, RuntimeSemver } from '@mulesoft/monitoring-center-components/lib/components/SupportedRuntimesHelper/SupportedRuntimesHelper'

export type ExternalValueProps = {
  organizationId: string
  environmentId: string
  resourceId: string
  targetId?: string
  isSaveButtonDisabled: boolean
}

export type ValueProps = ExternalValueProps & {
  logPointsConnectors: LogPointsConnectorsState
  logPointsConnectorsLoading: boolean
  connectorsLoading: boolean
  isSaving: boolean
  hasSavedYet: boolean
  resource?: Resource
  error?: any
}

export type EventProps = {
  fetchConnectors: (environmentId: string, resourceId: string, targetId?: string) => void
  save: (props: ExternalValueProps, logPointsConnectors: LogPointsConnectorsState) => void
  resetErrorAndSaved: () => void
}

export type Props = ValueProps & EventProps

export type State = {
  logPointsConnectors: LogPointsConnectorsState
  filteredLogPointsConnectors: LogPointsConnectorsState
  initialLoading: boolean
  query?: string
  muleVersion?: RuntimeSemver
}

class LogPointsApplicationsBody extends React.Component<Props, State> {

  private static listMainNote(muleVersion?: RuntimeSemver) {
    const major = muleVersion ? muleVersion.major : 3
    return `List of connectors supported by Log Points for applications using Mule ${major}.x`
  }

  private static connectorsHaveDiff(externalConnectors: LogPointsConnectorsState, internalConnectors: LogPointsConnectorsState) {
    return (externalConnectorKey: string) => {
      const externalConnector = externalConnectors[externalConnectorKey]
      const internalConnector = internalConnectors[externalConnectorKey]
      const externalChecked = externalConnector.checked
      const externalLogLevel = externalConnector.logLevel
      const internalChecked = internalConnector && internalConnector.checked
      const internalLogLevel = internalConnector && internalConnector.logLevel

      return (externalChecked !== internalChecked) || (externalLogLevel !== internalLogLevel)
    }
  }

  private static getTopBarMessageHref(resourceId: string, resource?: Resource): string | undefined {
    if (!resource) return undefined

    const queryParams = { origin: 'log-points' }
    const logsLinkProps = {key: 'appId', value: isARMHybridDetail(resource) || isCloudHub2Application(resource) ? getResourceName(resource) : resourceId, queryParams}
    const messageHref = buildLogSearchRedirectionLink(logsLinkProps)

    return messageHref
  }

  constructor(props: Props) {
    super(props)

    this.state = {
      logPointsConnectors: this.props.logPointsConnectors,
      filteredLogPointsConnectors: this.props.logPointsConnectors,
      initialLoading: true
    }

    this.hasChanges = this.hasChanges.bind(this)
    this.onLogLevelChange = this.onLogLevelChange.bind(this)
    this.onCheckboxChange = this.onCheckboxChange.bind(this)
    this.onQueryChange = this.onQueryChange.bind(this)
    this.getTopBarProps = this.getTopBarProps.bind(this)
    this.getTextFieldProps = this.getTextFieldProps.bind(this)
    this.getConnectorsListProps = this.getConnectorsListProps.bind(this)
    this.save = this.save.bind(this)

    if (!props.connectorsLoading && props.resource) props.fetchConnectors(props.environmentId, props.resourceId, props.targetId)
  }

  hasChanges() {
    const externalConnectors = this.props.logPointsConnectors
    const internalConnectors = this.state.logPointsConnectors

    if (!externalConnectors || !internalConnectors) return false

    const keys = Object.keys(externalConnectors)
    const foundDiff = keys.find(LogPointsApplicationsBody.connectorsHaveDiff(externalConnectors, internalConnectors))

    return foundDiff !== undefined
  }

  save() {
    this.props.save(this.props, this.state.logPointsConnectors)
  }

  onLogLevelChange(connectorId: string, logLevel: LogLevel) {
    if (this.props.error || this.props.hasSavedYet) this.props.resetErrorAndSaved()

    const connectors = this.state.logPointsConnectors
    const filteredConnectors = this.state.filteredLogPointsConnectors
    const connector = connectors[connectorId]

    if (connector) {
      this.setState({
        logPointsConnectors: {...connectors, [connectorId]: {...connector, logLevel: logLevel}},
        filteredLogPointsConnectors: {...filteredConnectors, [connectorId]: {...connector, logLevel: logLevel}},
        })
    }
  }

  onCheckboxChange(connectorId: string, checked: boolean) {
    if (this.props.error || this.props.hasSavedYet) this.props.resetErrorAndSaved()

    const connectors = this.state.logPointsConnectors
    const filteredConnectors = this.state.filteredLogPointsConnectors
    const connector = connectors[connectorId]
    const filteredConnector = filteredConnectors[connectorId]

    if (connector && filteredConnector) {
      this.setState({
        logPointsConnectors: {...connectors, [connectorId]: {...connector, checked: checked}},
        filteredLogPointsConnectors: {...filteredConnectors, [connectorId]: {...connector, checked: checked}}
      })
    }
  }

  onQueryChange(e: {value: string}) {
    const query = e.value.trim()
    const logPointsConnectors = {...this.state.logPointsConnectors}

    if (query) {
      // Filter connectors
      Object.keys(logPointsConnectors).forEach(connectorId => {
        if (!logPointsConnectors[connectorId].connector.name.toUpperCase().includes(query.toUpperCase())) {
          delete logPointsConnectors[connectorId]
        }
      })
    }

    this.setState({...this.state, filteredLogPointsConnectors: {...logPointsConnectors}})
  }

  getTopBarProps(): TopBarProps {
    const {isSaving, hasSavedYet, error, isSaveButtonDisabled, resourceId, resource} = this.props

    return {
      isSaving: isSaving,
      disabledButton: isSaveButtonDisabled,
      hasChanges: this.hasChanges(),
      hasSavedYet: hasSavedYet,
      error: error,
      save: this.save,
      messageHref: LogPointsApplicationsBody.getTopBarMessageHref(resourceId, resource),
      type: LogPointsType.APPLICATION
    }
  }

  getTextFieldProps() {
    return {
      className: 'log-points-applications-body-search-bar',
      placeholder: 'Search list...',
      type: 'search' as 'search',
      testId: 'log-points-applications-body-search-bar',
      onChange: this.onQueryChange,
      value: this.state.query
    }
  }

  getLogLevelOptions(resource:Resource): LogLevel[] {
    if (isARMHybridDetail(resource)) {
        return LOG_LEVEL_OPTIONS
    } else if (isCloudHub2Application(resource)){
        return LOG_LEVEL_OPTIONS.slice(1, 6)
    } else {
        return  LOG_LEVEL_OPTIONS.slice(1, 5)
    }
  }

  getConnectorsListProps(): ConnectorsListProps {
    return {
      logPointsConnectors: this.state.filteredLogPointsConnectors,
      logLevelOptions: this.props.resource ? this.getLogLevelOptions(this.props.resource) : LOG_LEVEL_OPTIONS.slice(1, 5),
      onLogLevelChange: this.onLogLevelChange,
      onCheckboxChange: this.onCheckboxChange,
      mainNote: LogPointsApplicationsBody.listMainNote(this.state.muleVersion)
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, prevContext: any): void {
    const {environmentId, resourceId, targetId, error, connectorsLoading, logPointsConnectorsLoading, resource, hasSavedYet} = this.props
    const resourcePropertiesChanged = prevProps.resourceId !== resourceId || prevProps.targetId !== targetId
    const finishedFetchingConnectors = prevProps.connectorsLoading && !connectorsLoading
    const finishedFetchingLogPointsConnectors = prevProps.logPointsConnectorsLoading && !logPointsConnectorsLoading
    const finishedLoadingResource = !prevProps.resource && resource && this.state.initialLoading

    if (resourcePropertiesChanged || finishedFetchingConnectors || finishedLoadingResource) {
      if (error || hasSavedYet) {
        this.props.resetErrorAndSaved()
      }

      const stringMuleVersion = getResourceMuleVersion(resource)
      const muleVersion = stringMuleVersion ? parseSemver(stringMuleVersion) : undefined

      if (finishedLoadingResource) { // Only first time load
        this.setState({...this.state, initialLoading: false, muleVersion})
      } else {
        this.setState({...this.state, muleVersion})
      }

      this.props.fetchConnectors(environmentId, resourceId, targetId)
    }

    if (finishedFetchingLogPointsConnectors) {
      this.setState({
        ...this.state, logPointsConnectors: this.props.logPointsConnectors, filteredLogPointsConnectors: {...this.props.logPointsConnectors}
      })
    }
  }

  render() {
    const {logPointsConnectorsLoading} = this.props
    const {initialLoading} = this.state
    const topBarProps = this.getTopBarProps()
    const textFieldProps = this.getTextFieldProps()
    const connectorsListProps = this.getConnectorsListProps()
    const isLoading = logPointsConnectorsLoading && initialLoading

    return (
      <div className="log-points-applications-body" data-test-id="log-points-applications-body">
        {
          !isLoading &&
          <div className="log-points-page-main-content-container" data-test-id="log-points-page-main-content-container">
              <div className="log-points-page-main-content-container-header" data-test-id="log-points-page-main-content-container-header">
                  <LogPointsTopBar {...topBarProps}/>
              </div>
              <div className="log-points-page-main-content-container-application-body" data-test-id="log-points-page-main-content-container-body">
                <TextField {...textFieldProps}/>
                <LogPointsConnectorsList {...connectorsListProps} />
              </div>
          </div>
        }
        {
          isLoading &&
          <div className="log-points-page-loading" data-test-id="log-points-page-loading">
              <Spinner/>
          </div>
        }
      </div>
    )
  }
}

export default LogPointsApplicationsBody
